/**
 * F-148 §10.3 (AC22): builds the discrepancy CSV client-side from the fetched receipt (UTF-8 BOM,
 * CRLF, quoted and formula-escaped text) and triggers a download. No mutation, no network.
 */
import { Download } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import {
  buildDiscrepancyCsv,
  discrepancyCsvFileName,
  type DiscrepancyCsvHeaders,
  type StockReceipt,
} from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';

export interface DiscrepancyDownloadButtonProps {
  readonly receipt: StockReceipt;
  /** Injectable for tests; defaults to an anchor click on a Blob URL. */
  readonly download?: (fileName: string, csv: string) => void;
  readonly className?: string;
}

function browserDownload(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function DiscrepancyDownloadButton({
  receipt,
  download = browserDownload,
  className,
}: DiscrepancyDownloadButtonProps): React.JSX.Element {
  const { t } = useTranslation();

  const handleClick = (): void => {
    const headers: DiscrepancyCsvHeaders = {
      lineNo: t('admin.receiving.export.lineNo'),
      product: t('admin.receiving.export.product'),
      variant: t('admin.receiving.export.variant'),
      barcode: t('admin.receiving.export.barcode'),
      supplierCode: t('admin.receiving.export.supplierCode'),
      supplierItemName: t('admin.receiving.export.supplierItemName'),
      expected: t('admin.receiving.export.expected'),
      accepted: t('admin.receiving.export.accepted'),
      damaged: t('admin.receiving.export.damaged'),
      missing: t('admin.receiving.export.missing'),
      corrected: t('admin.receiving.export.corrected'),
      netAccepted: t('admin.receiving.export.netAccepted'),
      note: t('admin.receiving.export.note'),
    };
    download(discrepancyCsvFileName(receipt.id), buildDiscrepancyCsv(receipt, headers));
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      className={className}
      data-testid="discrepancy-download"
    >
      <Download aria-hidden="true" className="size-4" />
      {t('admin.receiving.receipt.download')}
    </Button>
  );
}
